import { supabase } from './supabase';

export interface SavedReport {
  id: string;
  user_id: string;
  calculator_id: string;
  calculator_name: string;
  calculator_short_name: string;
  score: string;
  risk_level: string;
  interpretation: string;
  input_values: Record<string, unknown>;
  result_details: Record<string, string | number>;
  ai_interpretation: string;
  language: string;
  created_at: string;
  updated_at: string;
}

export interface SaveReportData {
  calculator_id: string;
  calculator_name: string;
  calculator_short_name: string;
  score: string | number;
  risk_level: string;
  interpretation: string;
  input_values: Record<string, unknown>;
  result_details: Record<string, string | number>;
  ai_interpretation: string;
  language?: string;
}

export const reportsService = {
  async saveReport(data: SaveReportData): Promise<{ data: SavedReport | null; error: Error | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { data: null, error: new Error('Please log in to save reports') };

      const { data: report, error } = await supabase
        .from('saved_reports')
        .insert({
          user_id: user.id,
          calculator_id: data.calculator_id,
          calculator_name: data.calculator_name,
          calculator_short_name: data.calculator_short_name,
          score: String(data.score),
          risk_level: data.risk_level,
          interpretation: data.interpretation,
          input_values: data.input_values,
          result_details: data.result_details,
          ai_interpretation: data.ai_interpretation,
          language: data.language || 'en'
        })
        .select()
        .single();

      if (error) throw error;
      return { data: report, error: null };
    } catch (err) {
      return { data: null, error: err instanceof Error ? err : new Error('Failed to save report') };
    }
  },

  async getReports(): Promise<{ data: SavedReport[]; error: Error | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { data: [], error: new Error('Please log in to view reports') };

      const { data: reports, error } = await supabase
        .from('saved_reports')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: reports || [], error: null };
    } catch (err) {
      return { data: [], error: err instanceof Error ? err : new Error('Failed to fetch reports') };
    }
  },

  async getReport(id: string): Promise<{ data: SavedReport | null; error: Error | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { data: null, error: new Error('Please log in to view reports') };

      const { data: report, error } = await supabase
        .from('saved_reports')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return { data: report, error: null };
    } catch (err) {
      return { data: null, error: err instanceof Error ? err : new Error('Failed to fetch report') };
    }
  },

  async deleteReport(id: string): Promise<{ success: boolean; error: Error | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { success: false, error: new Error('Please log in to delete reports') };

      const { error } = await supabase
        .from('saved_reports')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      return { success: true, error: null };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err : new Error('Failed to delete report') };
    }
  },

  async getReportCount(): Promise<{ count: number; error: Error | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { count: 0, error: null };

      const { count, error } = await supabase
        .from('saved_reports')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (error) throw error;
      return { count: count || 0, error: null };
    } catch (err) {
      return { count: 0, error: err instanceof Error ? err : new Error('Failed to fetch count') };
    }
  },

  async isAuthenticated(): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    return !!user;
  }
};
